import chroma from 'chroma-js';

import * as CPC from './colorscales';

// Source - https://stackoverflow.com/a/66902484
// Posted by STJ, modified by community. See post 'Timeline' for change history
// Retrieved 2026-03-03, License - CC BY-SA 4.0

function linspace(start, stop, num, endpoint = true) {
  const div = endpoint ? num - 1 : num;
  const step = (stop - start) / div;
  return Array.from({ length: num }, (_, i) => start + step * i);
}

function getColorscale(
  colorscale,
  nSwatches,
  logBreakpoints,
  log,
  colorscaleType,
) {
  /*
   * getColorscale() takes a scale, modifies it based on the input
   * parameters, and returns a new scale
   */
  // helper function repeats a categorical colorscale array N times
  const repeatArray = (array, n) => {
    let arrays = Array.apply(null, new Array(n));
    arrays = arrays.map(function () {
      return array;
    });
    return [].concat.apply([], arrays);
  };

  let cs = chroma.scale(colorscale).mode('lch');

  if (log) {
    const logData = Array(nSwatches)
      .fill()
      .map((x, i) => i + 1);
    cs = cs.classes(chroma.limits(logData, 'l', logBreakpoints));
  }

  let discreteScale = cs.colors(nSwatches);

  // repeat linear categorical ("qualitative") colorscales instead of repeating them
  if (!log && colorscaleType === 'categorical') {
    discreteScale = repeatArray(colorscale, nSwatches).slice(0, nSwatches);
  }

  return discreteScale;
}

const presetColormaps = Object.fromEntries(
  Object.entries({
    ...CPC.BUILTINS.sequential,
    ...CPC.BUILTINS.divergent,
    ...CPC.BUILTINS.categorical,
    ...CPC.BUILTINS.cyclical,
    ...CPC.CMOCEAN,
    ...CPC.OCEANUM,
    ...Object.assign(...CPC.BREWER.sequential.map((k) => ({ [k]: k }))),
    ...Object.assign(...CPC.BREWER.divergent.map((k) => ({ [k]: k }))),
    ...Object.assign(...CPC.BREWER.categorical.map((k) => ({ [k]: k }))),
  }).map(([k, v]) => [k.toLowerCase(), v]),
);

const getPresetColormap = (colormap, ncolors = 11) => {
  const preset =
    presetColormaps[colormap.name.toLowerCase()] || presetColormaps['thermal'];
  if (!preset.scale) {
    // scale is not defined in preset
    // If the domain is only 2 items, use them as the min and max over a range of ncolors
    const domain =
      colormap.domain.length === 2
        ? linspace(colormap.domain[0], colormap.domain[1], ncolors)
        : colormap.domain;
    const scale = getColorscale(preset, domain.length);

    return { scale, domain };
  } else {
    // scale is defined in preset
    if (colormap.domain) {
      if (preset.domain) {
        // Clip the preset scale to the domain
        const dmin = Math.min(...colormap.domain);
        const dmax = Math.max(...colormap.domain);
        const ind = preset.domain.map((d) => d >= dmin && d <= dmax);
        return {
          scale: preset.scale.filter((d, i) => ind[i]),
          domain: preset.domain.filter((d, i) => ind[i]),
        };
      } else {
        // Preset has colours but no numeric domain — resample to match user domain
        const n = colormap.domain.length;
        const scale = getColorscale(preset.scale, n);
        return { scale, domain: colormap.domain };
      }
    } else {
      return preset;
    }
  }
};

export { getPresetColormap, presetColormaps };
